import { useEffect, useState } from "react";
import { RefreshCw, SearchCheck } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Cost, PageHeader, Spinner } from "../components/AiHtml";
import { AuditReport } from "../components/AuditReport";
import { AuditComparison } from "../components/AuditComparison";
import { AuditPdfActions } from "../components/AuditPdf";
import { useAuth } from "../context/AuthContext";
import { useBrands } from "../context/BrandContext";
import { api, handleErr } from "../lib/api";

export default function Auditor() {
  const { refresh } = useAuth();
  const { active: brand, reload } = useBrands();
  const [url, setUrl] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setUrl(brand?.website || "");
    setReport(brand?.auditReport || null);
  }, [brand?.brandId, brand?.website, brand?.auditReport]);

  const isBrand = brand && url === brand.website;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = isBrand ? await api.post(`/brands/${brand.brandId}/audit`, {}) : await api.post("/audit", { website: url });
      setReport(data);
      if (isBrand) reload();
    } catch (e) { handleErr(e); } finally { setBusy(false); refresh(); }
  };

  return (
    <div className="space-y-6">
      <PageHeader label="SEO · AEO · GEO Auditor" title="Website audit" desc={brand ? `Active brand: ${brand.businessName}. Re-run the audit anytime to track progress.` : "Crawls your site and scores SEO, AEO and GEO readiness, with JSON-LD schema."} />
      <div className="card-n p-5 flex flex-col sm:flex-row gap-3">
        <Input data-testid="audit-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yourbrand.com" />
        <div className="flex items-center gap-3">
          <Cost n={10} />
          <Button data-testid="audit-run-btn" disabled={busy || !url.trim()} onClick={run} className="rounded-full whitespace-nowrap">
            {report ? <RefreshCw size={15} className="mr-1.5" /> : <SearchCheck size={15} className="mr-1.5" />}{report ? "Re-run audit" : "Run audit"}
          </Button>
        </div>
      </div>
      {!isBrand && url && brand && <p className="text-xs text-amber-400">This URL differs from the active brand, so the result won't be saved to its history.</p>}
      {busy ? <div className="card-n"><Spinner label="Crawling and auditing... (20-40s)" /></div> : (
        <>
          {isBrand && <AuditComparison brand={brand} />}
          {isBrand && report && brand.auditReport && <AuditPdfActions brand={brand} />}
          {report ? <AuditReport r={report} /> : <p className="text-slate-500 text-sm">No audit yet. Run one above.</p>}
        </>
      )}
    </div>
  );
}
